// Ekstern ballistikk (DOPE-kort) for DIREKTE ild — skarpskytter, spanskrytter og
// mitraljøse. Punkt-masse-modell i tre dimensjoner med G1-standard luftmotstand,
// numerisk integrert med RK4. Gir fall (drop), sidevind-avdrift (windage),
// flytid, restfart og treffenergi per avstand, samt et ferdig DOPE-kort med
// elevasjon/vindkorreksjon i MIL (NATO 6400) og MOA.
//
// Fysikken er ærlig: BC skalerer den kjente G1-motstandsfunksjonen (form-faktor),
// ingen tabell-fudge. Coriolis og spinn-avdrift (gyroskopisk) er BEVISST utelatt
// (dominerer først på ekstreme hold >1000 m) — modellen dekker HV-relevante hold.
/** Tyngdeakselerasjon (m/s²). */
export const G = 9.80665;
/** NATO-streker per radian (6400 mil / 2π). */
export const MIL_PER_RAD = 6400 / (2 * Math.PI);
/** Bueminutt (MOA) per radian (21600 / 2π). */
export const MOA_PER_RAD = 21600 / (2 * Math.PI);
/** G1-standardprosjektilets motstandskoeffisient Cd som funksjon av Mach-tall
 * (McCoy/Ingalls-tabell, dimensjonsløs). Interpoleres lineært. */
const G1_CD = [
    [0.0, 0.2629], [0.4, 0.2110], [0.5, 0.2088], [0.6, 0.2103], [0.7, 0.2224],
    [0.8, 0.2637], [0.9, 0.3583], [0.95, 0.4055], [1.0, 0.4644], [1.05, 0.5117],
    [1.1, 0.5386], [1.2, 0.5484], [1.3, 0.5453], [1.4, 0.5335], [1.5, 0.5175],
    [1.6, 0.5013], [1.8, 0.4692], [2.0, 0.4415], [2.2, 0.4194], [2.4, 0.4004],
    [2.6, 0.3855], [3.0, 0.3618], [4.0, 0.3222], [5.0, 0.3016],
];
/** G1 Cd ved et gitt Mach-tall (lineær interpolasjon, klemt til tabell-endene). */
export function g1DragCoefficient(mach) {
    if (mach <= G1_CD[0][0])
        return G1_CD[0][1];
    const last = G1_CD[G1_CD.length - 1];
    if (mach >= last[0])
        return last[1];
    for (let i = 1; i < G1_CD.length; i++) {
        const [m1, c1] = G1_CD[i];
        if (mach <= m1) {
            const [m0, c0] = G1_CD[i - 1];
            const t = (mach - m0) / (m1 - m0);
            return c0 + t * (c1 - c0);
        }
    }
    return last[1];
}
/** Omregning av ballistisk koeffisient fra imperisk (lb/in²) til metrisk (kg/m²). */
export function bcImperialToMetric(bcLbPerIn2) {
    return bcLbPerIn2 * 703.06957964; // 1 lb/in² = 703.0696 kg/m²
}
/** Standardatmosfære (ISA-tilnærming) for en høyde + temperatur. */
export function atmosphere(altitudeM = 0, tempC = 15) {
    // Trykk faller eksponentielt (skalahøyde ~8435 m); tetthet ∝ trykk/temperatur.
    const tempK = tempC + 273.15;
    const pressurePa = 101325 * Math.exp(-altitudeM / 8435);
    const densityKgM3 = pressurePa / (287.05 * tempK); // ideell gasslov, tørr luft
    const speedOfSoundMps = 331.3 * Math.sqrt(tempK / 273.15);
    return { densityKgM3, speedOfSoundMps };
}
export const STANDARD_ATMOSPHERE = { densityKgM3: 1.225, speedOfSoundMps: 340.3 };
/** Akselerasjonsvektor i en tilstand (tyngde + G1-motstand mot LUFT-fart). */
function accel(s, p, atm, wind) {
    // Luftfart = kulefart − vindfart (vind i horisontalplanet: x nedrekke, z side).
    const avx = s.vx - wind.wx;
    const avy = s.vy;
    const avz = s.vz - wind.wz;
    const speed = Math.hypot(avx, avy, avz);
    if (speed <= 0)
        return [0, -G, 0];
    const mach = speed / atm.speedOfSoundMps;
    const cd = g1DragCoefficient(mach);
    // a_drag = (π/8) · ρ · v² · Cd(M) / BC  (utledet fra BC = SD/i, SI-enheter).
    const aDrag = (Math.PI / 8) * atm.densityKgM3 * speed * speed * cd / p.bcMetric;
    return [
        -aDrag * (avx / speed),
        -G - aDrag * (avy / speed),
        -aDrag * (avz / speed),
    ];
}
/** Integrer banen (RK4). Returnerer prøvepunkter for hvert `stepM` samt
 * sluttpunktet der kula treffer bakken (dropM ≤ −stopBelowLosM) eller når
 * maxRange. dropM måles relativt en horisontal siktelinje gjennom siktet. */
export function trajectory(p, opts) {
    const atm = opts.atmosphere ?? STANDARD_ATMOSPHERE;
    const sightHeight = opts.sightHeightM ?? 0.05;
    const step = opts.stepM ?? 50;
    const dt = opts.dtS ?? 0.0005;
    const floorDrop = opts.stopBelowLosM ?? 3;
    const rad = wind2vec(opts.wind);
    // Løpet sitter sightHeight UNDER siktelinjen; siktelinjen er x-aksen (y=0).
    let s = {
        x: 0, y: -sightHeight, z: 0,
        vx: p.muzzleVelocityMps * Math.cos(opts.boreAngleRad),
        vy: p.muzzleVelocityMps * Math.sin(opts.boreAngleRad),
        vz: 0,
    };
    const out = [];
    let nextSample = step;
    const sample = (st, t) => {
        const v = Math.hypot(st.vx, st.vy, st.vz);
        const pt = {
            timeS: t, rangeM: st.x, dropM: st.y, driftM: st.z,
            velocityMps: v, machNumber: v / atm.speedOfSoundMps,
        };
        if (p.massKg !== undefined)
            pt.energyJ = 0.5 * p.massKg * v * v;
        out.push(pt);
    };
    let t = 0;
    let guard = 0;
    const maxSteps = Math.ceil((opts.maxRangeM / Math.max(1, p.muzzleVelocityMps * 0.1)) / dt) + 2_000_000;
    while (s.x < opts.maxRangeM && s.y > -(sightHeight + floorDrop) && guard++ < maxSteps) {
        s = rk4(s, p, atm, rad, dt);
        t += dt;
        if (s.x >= nextSample) {
            sample(s, t);
            nextSample += step;
        }
    }
    // Sluttpunkt — men unngå duplikat hvis siste steg allerede ble prøvetatt.
    if (!out.length || out[out.length - 1].timeS < t)
        sample(s, t);
    return out;
}
function rk4(s, p, atm, wind, dt) {
    const add = (a, k, h) => ({
        x: a.x + (k.x ?? 0) * h, y: a.y + (k.y ?? 0) * h, z: a.z + (k.z ?? 0) * h,
        vx: a.vx + (k.vx ?? 0) * h, vy: a.vy + (k.vy ?? 0) * h, vz: a.vz + (k.vz ?? 0) * h,
    });
    const deriv = (st) => {
        const [ax, ay, az] = accel(st, p, atm, wind);
        return { x: st.vx, y: st.vy, z: st.vz, vx: ax, vy: ay, vz: az };
    };
    const k1 = deriv(s);
    const k2 = deriv(add(s, k1, dt / 2));
    const k3 = deriv(add(s, k2, dt / 2));
    const k4 = deriv(add(s, k3, dt));
    return {
        x: s.x + (dt / 6) * (k1.x + 2 * k2.x + 2 * k3.x + k4.x),
        y: s.y + (dt / 6) * (k1.y + 2 * k2.y + 2 * k3.y + k4.y),
        z: s.z + (dt / 6) * (k1.z + 2 * k2.z + 2 * k3.z + k4.z),
        vx: s.vx + (dt / 6) * (k1.vx + 2 * k2.vx + 2 * k3.vx + k4.vx),
        vy: s.vy + (dt / 6) * (k1.vy + 2 * k2.vy + 2 * k3.vy + k4.vy),
        vz: s.vz + (dt / 6) * (k1.vz + 2 * k2.vz + 2 * k3.vz + k4.vz),
    };
}
/** Vind → (nedrekke-komponent wx, side-komponent wz) i m/s. fromDeg = retning
 * vinden kommer fra (0 = ren motvind, 90 = fra høyre). */
function wind2vec(w) {
    if (!w || w.speedMps === 0)
        return { wx: 0, wz: 0 };
    const r = (w.fromDeg * Math.PI) / 180;
    // Motvind (0°) gir negativ wx (blåser MOT kula → luftfart øker nedrekke).
    // Fra høyre (90°) gir wz negativ → skyver kula mot venstre (dvs. positiv drift
    // beregnes av at kula lener seg inn i vinden). Vi returnerer vindens
    // hastighetsvektor i banekoordinater.
    return { wx: -w.speedMps * Math.cos(r), wz: -w.speedMps * Math.sin(r) };
}
/** Finn løp-elevasjonen (rad) som nuller kula på `zeroRangeM` (kryssar
 * siktelinjen y=0). Binærsøk på banefallet ved nullavstanden. */
export function zeroAngle(p, zeroRangeM, opts) {
    const dropAt = (angle) => {
        const tr = trajectory(p, {
            boreAngleRad: angle, maxRangeM: zeroRangeM + 1, stepM: zeroRangeM,
            sightHeightM: opts?.sightHeightM, atmosphere: opts?.atmosphere, wind: opts?.wind, dtS: opts?.dtS,
        });
        // Interpoler dropM ved nøyaktig zeroRange.
        return interpDrop(tr, zeroRangeM);
    };
    let lo = 0, hi = 0.05; // 0..~2.9° dekker vanlige hold; utvid ved behov
    while (dropAt(hi) < 0 && hi < 0.3)
        hi *= 2;
    for (let i = 0; i < 60; i++) {
        const mid = (lo + hi) / 2;
        if (dropAt(mid) < 0)
            lo = mid;
        else
            hi = mid;
    }
    return (lo + hi) / 2;
}
/** Lineær interpolasjon av dropM ved en avstand fra en bane. */
function interpDrop(tr, rangeM) {
    for (let i = 1; i < tr.length; i++) {
        if (tr[i].rangeM >= rangeM) {
            const a = tr[i - 1], b = tr[i];
            const t = (rangeM - a.rangeM) / (b.rangeM - a.rangeM || 1);
            return a.dropM + t * (b.dropM - a.dropM);
        }
    }
    return tr[tr.length - 1]?.dropM ?? 0;
}
/** Ferdig DOPE-kort: for hvert `stepM` hold gir det elevasjon + vindkorreksjon
 * i MIL/MOA, restfart, flytid og energi. Kula er nullet på `zeroRangeM`. */
export function dopeCard(p, opts) {
    const step = opts.stepM ?? 100;
    const angle = zeroAngle(p, opts.zeroRangeM, opts);
    const tr = trajectory(p, {
        boreAngleRad: angle, maxRangeM: opts.maxRangeM + step, stepM: 5,
        sightHeightM: opts.sightHeightM, atmosphere: opts.atmosphere, wind: opts.wind,
        stopBelowLosM: 1e6, // DOPE trenger hele fallet, ikke bakke-stopp
    });
    const rows = [];
    for (let r = step; r <= opts.maxRangeM; r += step) {
        const pt = interpPoint(tr, r);
        if (!pt)
            break;
        const dropRad = -pt.dropM / r; // vinkel som må legges til for å treffe
        const windRad = -pt.driftM / r; // motsatt av avdriften
        rows.push({
            rangeM: r,
            dropCm: pt.dropM * 100,
            elevationMil: dropRad * MIL_PER_RAD,
            elevationMoa: dropRad * MOA_PER_RAD,
            windageMil: windRad * MIL_PER_RAD,
            windageCm: -pt.driftM * 100, // korreksjon = motsatt av avdriften
            driftCm: pt.driftM * 100,
            velocityMps: pt.velocityMps,
            machNumber: pt.machNumber,
            timeOfFlightS: pt.timeS,
            ...(pt.energyJ !== undefined ? { energyJ: pt.energyJ } : {}),
        });
    }
    return rows;
}
/** Interpoler et fullt banepunkt ved en avstand. */
function interpPoint(tr, rangeM) {
    for (let i = 1; i < tr.length; i++) {
        if (tr[i].rangeM >= rangeM) {
            const a = tr[i - 1], b = tr[i];
            const t = (rangeM - a.rangeM) / (b.rangeM - a.rangeM || 1);
            const lerp = (x, y) => x + t * (y - x);
            const out = {
                timeS: lerp(a.timeS, b.timeS), rangeM,
                dropM: lerp(a.dropM, b.dropM), driftM: lerp(a.driftM, b.driftM),
                velocityMps: lerp(a.velocityMps, b.velocityMps),
                machNumber: lerp(a.machNumber, b.machNumber),
            };
            if (a.energyJ !== undefined && b.energyJ !== undefined)
                out.energyJ = lerp(a.energyJ, b.energyJ);
            return out;
        }
    }
    return null;
}
/** Maks banehøyde (max ordinate) over siktelinjen (m) for en gitt bane. */
export function maxOrdinateM(tr) {
    return tr.reduce((mx, pt) => Math.max(mx, pt.dropM), -Infinity);
}
/** Nøkkeltall for en patron nullet på zeroRange, ut til maxRange. */
export function ballisticSummary(p, opts) {
    const angle = zeroAngle(p, opts.zeroRangeM, opts);
    const tr = trajectory(p, {
        boreAngleRad: angle, maxRangeM: opts.maxRangeM, stepM: 5,
        sightHeightM: opts.sightHeightM, atmosphere: opts.atmosphere, wind: opts.wind,
        stopBelowLosM: 1e6,
    });
    let supersonic = 0;
    for (const pt of tr) {
        if (pt.machNumber >= 1)
            supersonic = pt.rangeM;
        else
            break;
    }
    return {
        zeroAngleMil: angle * MIL_PER_RAD,
        maxOrdinateCm: maxOrdinateM(tr) * 100,
        supersonicRangeM: supersonic,
    };
}
//# sourceMappingURL=ballistics.js.map